import React from 'react';
import { Col, Form, Row, SearchableSelect } from '@/components/ui';
import type {
  HiredEquipmentEntryMethod,
  TaskOption,
  VendorOption,
} from '../types';
import type { HiredEquipmentPurchaseOrder } from '../types';
import { formatPoOptionLabel } from '../utils/poDisplay';
import HiredEquipmentTaskPicker from './HiredEquipmentTaskPicker';

export interface EntryMethodFieldsProps {
  entryMethod: HiredEquipmentEntryMethod;
  poEnabled: boolean;
  vendors: VendorOption[];
  vendorsLoading: boolean;
  /** H9/M26: set when the vendor list failed to load; shown instead of an empty dropdown. */
  vendorsError?: string | null;
  vendorId: number | '';
  onVendorChange: (id: number | '') => void;
  purchaseOrders: HiredEquipmentPurchaseOrder[];
  posLoading: boolean;
  /** H9: set when the PO list failed to load; never shown as "No active POs". */
  posError?: string | null;
  poId: number | '';
  onPoChange: (id: number | '') => void;
  selectedTask: TaskOption | null;
  onTaskChange: (task: TaskOption | null) => void;
  fetchTasks: (query: string) => Promise<TaskOption[]>;
  prePopulate: boolean;
  onPrePopulateChange: (checked: boolean) => void;
}

const RequiredMark: React.FC = () => <span className="tw-text-danger">*</span>;

const EntryMethodFields: React.FC<EntryMethodFieldsProps> = ({
  entryMethod,
  poEnabled,
  vendors,
  vendorsLoading,
  vendorsError = null,
  vendorId,
  onVendorChange,
  purchaseOrders,
  posLoading,
  posError = null,
  poId,
  onPoChange,
  selectedTask,
  onTaskChange,
  fetchTasks,
  prePopulate,
  onPrePopulateChange,
}) => {
  const vendorSelect = (
    <Form.Group controlId="he-vendor">
      <Form.Label>
        Subcontractor / Vendor <RequiredMark />
      </Form.Label>
      {/* V2-045 (FORM-03): about 280 vendors – type part of the name to narrow the list. */}
      <SearchableSelect
        id="he-vendor"
        options={vendors.map((vendor) => ({ value: vendor.id, label: vendor.name }))}
        value={vendorId}
        onChange={onVendorChange}
        disabled={vendorsLoading || !!vendorsError}
        isInvalid={!!vendorsError}
        placeholder={
          vendorsLoading ? 'Loading vendors…' : vendorsError ? 'Vendors unavailable' : 'Type to search vendors'
        }
        noMatchText="No vendor matches"
      />
      {vendorsError ? (
        <p role="alert" className="tw-mb-0 tw-mt-1 tw-text-sm tw-text-danger">
          {vendorsError}
        </p>
      ) : (
        // L4: the PO hint only makes sense when there is a PO list to narrow.
        <Form.Text>
          {entryMethod === 'purchase_order'
            ? 'Vendor first – narrows the PO list.'
            : 'Choose the subcontractor for this submission.'}
        </Form.Text>
      )}
    </Form.Group>
  );

  const selectedPo = typeof poId === 'number' ? purchaseOrders.find((po) => po.id === poId) : undefined;
  const selectedPoDescription = selectedPo?.description?.trim() || '';

  const poSelect = (
    <Form.Group controlId="he-po">
      <Form.Label>
        Purchase Order {poEnabled ? <RequiredMark /> : null}
      </Form.Label>
      <Form.Select
        value={poId === '' ? '' : String(poId)}
        disabled={!vendorId || posLoading || !poEnabled || !!posError}
        isInvalid={!!posError}
        onChange={(e) => {
          const next = e.target.value;
          onPoChange(next ? Number(next) : '');
        }}
      >
        <option value="">
          {!vendorId
            ? 'Pick a vendor first'
            : posLoading
              ? 'Loading POs…'
              : posError
                ? 'Purchase orders unavailable'
                : !poEnabled
                ? 'POs not enabled – add lines manually'
                : purchaseOrders.length === 0
                  ? 'No active POs for this vendor'
                  : 'Select purchase order...'}
        </option>
        {purchaseOrders.map((po) => (
          <option key={po.id} value={po.id}>
            {formatPoOptionLabel(po)}
          </option>
        ))}
      </Form.Select>
      {posError ? (
        <p role="alert" className="tw-mb-0 tw-mt-1 tw-text-sm tw-text-danger">
          {posError}
        </p>
      ) : selectedPoDescription ? (
        <p className="tw-mb-0 tw-mt-1 tw-text-sm tw-text-content">{selectedPoDescription}</p>
      ) : (
        <Form.Text>
          {poEnabled ? 'Active POs for the selected vendor.' : "POs aren't enabled – this field is optional."}
        </Form.Text>
      )}
    </Form.Group>
  );

  const taskSelect = (
    <Form.Group controlId="he-task">
      <Form.Label>
        Task <RequiredMark />
      </Form.Label>
      <HiredEquipmentTaskPicker
        selected={selectedTask}
        onChange={onTaskChange}
        searchTasks={fetchTasks}
      />
      <Form.Text>Search by task number or description. The project is shown when the task has one.</Form.Text>
    </Form.Group>
  );

  if (entryMethod === 'purchase_order') {
    return (
      <>
        <Row>
          <Col md={6}>{vendorSelect}</Col>
          <Col md={6}>{poSelect}</Col>
        </Row>
        <Form.Check
          id="he-prepopulate"
          checked={prePopulate}
          disabled={!poEnabled}
          onChange={(e) => onPrePopulateChange(e.currentTarget.checked)}
          label="Pre-populate the grid with this PO's resources (recommended)"
        />
      </>
    );
  }

  if (entryMethod === 'subcontractor') {
    return (
      <Row>
        <Col md={6}>{vendorSelect}</Col>
      </Row>
    );
  }

  return (
    <Row>
      <Col md={6}>{taskSelect}</Col>
    </Row>
  );
};

export default EntryMethodFields;
